import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Clock, Zap, Flame, ArrowRight, XCircle } from 'lucide-react';
import { useMotionBudget } from '../../../app/hooks/useMotionBudget';
import { DjayAvatar, type DjayEmotion } from '../../../mascot';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { type SessionResult } from '../sessionSummaryData';
import { type FamilyMasteryState, getGrowthStage } from '../../../mastery';
import { GrowthStageIcon } from '../../../components/shared/GrowthStageIcon';
import { FAMILY_DISPLAY_NAMES } from '../../../content/familyLabels';
import { SummaryShell } from './SummaryShell';
import { ScoreDotRow } from './ScoreDot';
interface QuickRecapProps {
  sessionAnswers: SessionResult[];
  xpGained: number;
  streak: number;
  djayTip: string;
  djayEmotion: string;
  preFamilyMastery: Record<string, FamilyMasteryState>;
  currentFamilyMastery: Record<string, FamilyMasteryState>;
  onNextRound: () => void;
  onExit: () => void;
  onReviewErrors?: () => void;
  onReviewQuestion?: (index: number) => void;
  /** Compact mode strips Djay-bubble + mastery-line for 2nd-3rd session today (§19.6). */
  compact?: boolean;
}
export function QuickRecap({
  sessionAnswers,
  xpGained,
  streak,
  djayTip,
  djayEmotion,
  preFamilyMastery,
  currentFamilyMastery,
  onNextRound,
  onExit,
  onReviewErrors,
  onReviewQuestion,
  compact = false
}: QuickRecapProps) {
  // ── Reflective tap (§16.G) ──
  // After a session, offer a no-consequence tap to capture how the round
  // felt. Three emoji-buttons, single-select, persists locally for future
  // surfacing in progress. Mounts inline below the stats so it does not
  // hijack focus from the "Volgende ronde" button. Skipped in compact mode
  // (2nd/3rd session of the day) per QuickRecap's existing density rule.
  const [reflection, setReflection] = useState<ReflectionMood | null>(null);
  const correctCount = sessionAnswers.filter(a => a.isCorrect).length;
  const totalCount = sessionAnswers.length;
  const incorrectCount = totalCount - correctCount;
  const totalTimeMs = sessionAnswers.reduce((sum, a) => sum + (a.timeMs || 0), 0);
  // Show total time as M:SS for sessions ≥1 min, otherwise as average sec/vraag
  // (more informative for short sessions and prevents weird "0:18 min" rendering).
  const hasTime = totalTimeMs > 0 && totalCount > 0;
  const showAsAvg = hasTime && totalTimeMs < 60000;
  const timeLabel = hasTime ? showAsAvg ? `~${Math.max(1, Math.round(totalTimeMs / totalCount / 1000))} sec` : `${Math.floor(totalTimeMs / 60000)}:${String(Math.floor(totalTimeMs % 60000 / 1000)).padStart(2, '0')} min` : null;
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      nextBtnRef.current?.focus();
    }, 1200);
    return () => clearTimeout(timer);
  }, []);
  // Find one significant mastery change
  let masteryChange = null as null | {
    familyId: string;
    oldStage: ReturnType<typeof getGrowthStage>;
    newStage: ReturnType<typeof getGrowthStage>;
    delta: number;
  };
  if (!compact) {
    const practicedFamilyIds = Array.from(new Set(sessionAnswers.map(a => a.familyId).filter(Boolean))) as string[];
    for (const familyId of practicedFamilyIds) {
      const preKey = Object.keys(preFamilyMastery).find(k => k.startsWith(familyId));
      const postKey = Object.keys(currentFamilyMastery).find(k => k.startsWith(familyId));
      const preScore = preKey ? preFamilyMastery[preKey].masteryScore : 0;
      const postScore = postKey ? currentFamilyMastery[postKey].masteryScore : 0;
      const oldStage = getGrowthStage(preScore);
      const newStage = getGrowthStage(postScore);
      if (newStage.level > oldStage.level || postScore - preScore > 5) {
        masteryChange = {
          familyId,
          oldStage,
          newStage,
          delta: postScore - preScore
        };
        break;
      }
    }
  }
  return <SummaryShell tone="quick" data-id="element-11059">
      <div className="flex w-full max-w-md flex-col items-center" data-id="element-11060">
        {/* Djay & Tip */}
        {!compact && <div className="mb-6 flex w-full items-start gap-3" data-id="element-11061">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          delay: 0.1
        }} className="shrink-0" data-id="element-11062">
              <DjayAvatar emotion={djayEmotion as DjayEmotion} size="md" data-id="element-11063" />
            </motion.div>
            <motion.div initial={{
          opacity: 0,
          x: -12
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.9,
          duration: 0.3
        }} className="flex-1 rounded-2xl rounded-tl-none border border-slate-100 bg-white p-3 text-sm font-medium text-slate-700 shadow-sm" aria-live="polite" data-id="element-11064">
              {djayTip}
            </motion.div>
          </div>}

        {/* Score row */}
        <div className="mb-6 flex flex-col items-center" data-id="element-11065">
          <ScoreDotRow results={sessionAnswers} onDotClick={onReviewQuestion} baseDelay={0.15} data-id="element-11066" />
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.55
        }} className="mt-3 text-xl font-bold text-slate-800" data-id="element-11067">
            {correctCount} van de {totalCount} goed
          </motion.p>
        </div>

        {/* Stats trio */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45
      }} className="mb-5 flex w-full gap-3" data-id="element-11068">
          {timeLabel && <Card className="flex flex-1 flex-col items-center justify-center border-slate-100 bg-slate-50/60 p-3" data-id="element-11069">
              <Clock className="mb-1 h-5 w-5 text-slate-400" data-id="element-11070" />
              <span className="font-semibold text-slate-700" data-id="element-11071">{timeLabel}</span>
            </Card>}
          {xpGained > 0 && <Card className="flex flex-1 flex-col items-center justify-center border-indigo-100 bg-indigo-50/60 p-3" data-id="element-11072">
              <Zap className="mb-1 h-5 w-5 text-indigo-400" data-id="element-11073" />
              <span className="font-semibold text-indigo-700" data-id="element-11074">
                +{xpGained} XP
              </span>
            </Card>}
          {streak > 0 && <Card className="flex flex-1 flex-col items-center justify-center border-orange-100 bg-orange-50/60 p-3" data-id="element-11075">
              <Flame className="mb-1 h-5 w-5 text-orange-400" data-id="element-11076" />
              <span className="font-semibold text-orange-700" data-id="element-11077">
                {streak} {streak === 1 ? 'dag' : 'dgn'}
              </span>
            </Card>}
        </motion.div>

        {/* Mastery delta */}
        {masteryChange && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} transition={{
        delay: 0.7
      }} className="mb-6 w-full overflow-hidden" data-id="element-11078">
            <Card className="flex items-center gap-3 border-emerald-100 bg-emerald-50/40 p-3" data-id="element-11079">
              <div className="flex items-center gap-1.5" data-id="element-11080">
                <GrowthStageIcon stage={masteryChange.oldStage.level} className="h-7 w-7 opacity-50 grayscale" data-id="element-11081" />
                <ArrowRight className="h-3.5 w-3.5 text-slate-300" data-id="element-11082" />
                <GrowthStageIcon stage={masteryChange.newStage.level} className="h-9 w-9" data-id="element-11083" />
              </div>
              <div className="min-w-0" data-id="element-11084">
                <p className="truncate text-xs font-medium text-slate-500" data-id="element-11085">
                  {FAMILY_DISPLAY_NAMES[masteryChange.familyId] ?? masteryChange.familyId}
                </p>
                <p className="font-bold text-emerald-700" data-id="element-11086">
                  Nu {masteryChange.newStage.label}!
                </p>
              </div>
            </Card>
          </motion.div>}

        {/* Reflective tap (§16.G) — opt-in, no consequence */}
        {!compact && <ReflectionTap selected={reflection} onSelect={mood => {
        setReflection(mood);
        persistReflection(mood);
      }} data-id="element-11087" />}

        {/* Actions */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.0
      }} className="flex w-full flex-col gap-3" data-id="element-11088">
          <Button ref={nextBtnRef} size="xl" onClick={onNextRound} className="w-full text-lg shadow-lg shadow-indigo-200" data-id="element-11089">
            Volgende ronde <ArrowRight className="ml-2 h-5 w-5" data-id="element-11090" />
          </Button>

          <div className="flex items-center justify-between px-1" data-id="element-11091">
            <button type="button" onClick={onExit} className="py-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700" data-id="element-11092">
              Klaar voor nu
            </button>

            {totalCount > 0 && onReviewErrors && <button type="button" onClick={onReviewErrors} className="flex items-center gap-1.5 py-2 text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-700" data-id="element-11093">
                <XCircle className="h-4 w-4" data-id="element-11094" />
                {incorrectCount > 0 ? `Bekijk fouten (${incorrectCount})` : 'Bekijk antwoorden'}
              </button>}
          </div>
        </motion.div>
      </div>
    </SummaryShell>;
}
// ── ReflectionTap (§16.G) ────────────────────────────────────────────────
/**
 * Three-option mood tap shown at the bottom of a successful QuickRecap.
 *
 * Pure metacognitie — no XP, no progression impact, no validation. The
 * learner taps how the session felt; we persist locally so a future surface
 * (parent portal, voortgang trends) can use it without affecting any
 * existing flows. One-tap; the row stays visible after selection so the
 * learner can change their mind without "did it save?"-anxiety.
 *
 * Animation respects useMotionBudget; selection has aria-pressed for SR.
 */
type ReflectionMood = 'easy' | 'good' | 'hard';
const REFLECTION_OPTIONS: Array<{
  mood: ReflectionMood;
  emoji: string;
  label: string;
}> = [{
  mood: 'easy',
  emoji: '😎',
  label: 'Makkie'
}, {
  mood: 'good',
  emoji: '😊',
  label: 'Fijn'
}, {
  mood: 'hard',
  emoji: '😅',
  label: 'Pittig'
}];
function ReflectionTap({
  selected,
  onSelect
}: {
  selected: ReflectionMood | null;
  onSelect: (mood: ReflectionMood) => void;
}) {
  const {
    shouldAnimate
  } = useMotionBudget();
  return <motion.div initial={shouldAnimate ? {
    opacity: 0,
    y: 8
  } : false} animate={shouldAnimate ? {
    opacity: 1,
    y: 0
  } : false} transition={{
    delay: 0.85,
    duration: 0.3
  }} className="mb-4 w-full" data-id="element-11095">
      <p className="mb-2 text-center text-xs font-medium text-slate-500" data-id="element-11096">
        Hoe voelde dat?
      </p>
      <div className="flex w-full items-center justify-center gap-2" role="radiogroup" aria-label="Reflectie op deze sessie" data-id="element-11097">
        {REFLECTION_OPTIONS.map(opt => {
        const isSelected = selected === opt.mood;
        return <motion.button key={opt.mood} type="button" role="radio" aria-checked={isSelected} aria-label={opt.label} onClick={() => onSelect(opt.mood)} whileHover={shouldAnimate ? {
          y: -2
        } : undefined} whileTap={shouldAnimate ? {
          scale: 0.92
        } : undefined} animate={isSelected && shouldAnimate ? {
          scale: [1, 1.15, 1],
          transition: {
            duration: 0.35
          }
        } : undefined} className={`flex flex-col items-center justify-center gap-0.5 rounded-2xl border px-3 py-2 text-xs transition-colors ${isSelected ? 'border-indigo-300 bg-indigo-50 text-indigo-700 shadow-sm' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-11098">
              <span aria-hidden className="text-2xl leading-none" data-id="element-11099">
                {opt.emoji}
              </span>
              <span className="font-medium" data-id="element-11100">{opt.label}</span>
            </motion.button>;
      })}
      </div>
    </motion.div>;
}
/**
 * Persist reflection locally. We keep the last 50 reflections so a future
 * voortgang surface can chart self-reported difficulty without needing a
 * backend change. Failures are swallowed — this is a soft signal, not
 * critical-path data.
 */
function persistReflection(mood: ReflectionMood) {
  try {
    const KEY = 'mp:reflection:log:v1';
    const raw = localStorage.getItem(KEY);
    const log: Array<{
      mood: ReflectionMood;
      at: number;
    }> = raw ? JSON.parse(raw) : [];
    log.push({
      mood,
      at: Date.now()
    });
    // Cap to 50 most recent entries
    const trimmed = log.slice(-50);
    localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch {
    // Storage full / unavailable — silently ignore, reflection is optional
  }
}